import { ReportService, type ReportDO } from "./report.service"

export class ReportEmbed extends HTMLElement {
    private readonly slug: string = document.body.getAttribute("slug") ?? ""
    private readonly language: string | null = this.getAttribute("language")
    private observer: IntersectionObserver | null = null
    private started = false

    constructor(private reportService: ReportService = window.tbwServices.reportService) {
        super()
    }

    connectedCallback() {
        if (!this.language || !this.verifyInput()) { return }
        // Lazy mount: the report body is a ~5MB PDF rendered natively in an <iframe>.
        // The page holds one <report-embed> per language tab, all in the DOM at once.
        // Mounting every language's iframe on page load ran several concurrent native
        // PDF renders and froze the renderer. IntersectionObserver never fires for a
        // display:none tab pane, so only the ACTIVE tab renders — and switching tabs
        // mounts the new language on demand, the first time it becomes visible.
        this.observer = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                void this.start()
            }
        }, { rootMargin: "200px" })
        this.observer.observe(this)
    }

    disconnectedCallback() {
        this.observer?.disconnect()
        this.observer = null
    }

    private async start() {
        if (this.started) { return }
        this.started = true
        this.observer?.disconnect()
        this.observer = null
        this.innerHTML = '<p class="report-embed-loading">Loading...</p>'
        await this.waitForAuth()
        await this.render()
    }

    /** The auth check may not be ready on the first intersection tick; wait for it
     *  (bounded) so the visible tab still renders instead of stalling on "Loading...". */
    private async waitForAuth(timeoutMs = 15000): Promise<void> {
        const start = Date.now()
        while (!window.tbwServices.authService.isAuthenticated) {
            if (Date.now() - start > timeoutMs) { return }
            await new Promise((r) => setTimeout(r, 200))
        }
    }

    verifyInput() {
        return this.language === "fr" || this.language === "en" || this.language === "de"
    }

    async render() {
        this.innerHTML = '<p class="report-embed-loading">Loading...</p>'
        try {
            const content = await this.getReportUrl()
            const url = this.language === 'en' ? content.englishURL
                      : this.language === 'de' ? content.germanURL
                      : content.frenchURL
            if (!url) {
                // No PDF for this language. Never build an <iframe src="undefined">:
                // it resolves to /reports/undefined and recursively loads the whole app.
                this.innerHTML = '<p class="report-embed-unavailable">This report is not available in this language yet.</p>'
                return
            }
            // iOS Safari can't display PDFs inline in an <iframe> (shows blank).
            // Offer an explicit "Open the PDF" button that opens the native viewer.
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
                || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
            const viewer = isIOS
                ? `<div class="report-embed-ios" style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;height:100%;min-height:320px;padding:32px;text-align:center;">
                       <p style="margin:0;font-size:14px;color:#6b6880;max-width:32ch;">Open the report to read it full-screen.</p>
                       <a href="${url}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:8px;padding:13px 26px;background:#3442EB;color:#fff;text-decoration:none;border-radius:10px;font-size:15px;font-weight:600;">Open the PDF ↗</a>
                   </div>`
                : `<iframe src="${url}" style="width: 100%; height: 100%; border: none; flex: 1;"></iframe>`
            this.innerHTML = `
        <tbw-auth-state>
            <div slot="premium" style="width: 100%; height: 100%; display: flex; flex-direction: column;">
                <div class="report-embed" style="width: 100%; height: 100%; display: flex; flex-direction: column;">
                    ${viewer}
                </div>
            </div>
        </tbw-auth-state>
        `
        } catch {
            this.innerHTML = '<p class="report-embed-error">Unable to load report</p>'
        }
    }

    async getReportUrl(): Promise<ReportDO> {
        const content = await this.reportService.get(this.slug)
        return content
    }

}